/**
 * The Metro 2 code sets a servicer picks from, spelled the way the Credit Reporting Resource Guide
 * spells them.
 *
 * Only the codes a mortgage servicer actually reaches for are here. The full guide carries several
 * hundred, most of them for card and retail furnishers, and a dropdown of those is a worse tool
 * than a short one: every extra code is another way to put something wrong on a credit file.
 */

/** Portfolio Type (base segment field 8). */
export const PORTFOLIO_TYPES: Record<string, string> = {
  M: 'M — Mortgage',
  I: 'I — Installment',
  C: 'C — Line of Credit',
  O: 'O — Open',
  R: 'R — Revolving',
}

/** Account Type (field 9). */
export const ACCOUNT_TYPES: Record<string, string> = {
  '26': '26 — Conventional Real Estate Mortgage',
  '19': '19 — FHA Real Estate Mortgage',
  '25': '25 — VA Real Estate Mortgage',
  '5A': '5A — Real Estate, Junior Liens and Unsecured',
  '02': '02 — Secured',
  '89': '89 — Home Improvement',
}

/** ECOA Code (field 38): whose liability the account is. */
export const ECOA_CODES: Record<string, string> = {
  '1': '1 — Individual',
  '2': '2 — Joint contractual liability',
  '3': '3 — Authorized user',
  '5': '5 — Co-maker or guarantor',
  '7': '7 — Maker',
  T: 'T — Relationship terminated',
  X: 'X — Deceased',
  Z: 'Z — Delete consumer',
}

/**
 * Account Status (field 17). The delinquency ladder is derived from the loan every month, so the
 * override list is the statuses that describe a decision instead: the account ended, or ended
 * badly.
 */
export const ACCOUNT_STATUS_LABELS: Record<string, string> = {
  '11': 'Current, paid as agreed',
  '13': 'Paid or closed, zero balance',
  '05': 'Transferred to another lender',
  '61': 'Paid in full, was a voluntary surrender',
  '62': 'Paid in full, was a collection',
  '63': 'Paid in full, was a repossession',
  '64': 'Paid in full, was a charge-off',
  '65': 'Paid in full, a foreclosure was started',
  '71': '30-59 days past due',
  '78': '60-89 days past due',
  '80': '90-119 days past due',
  '82': '120-149 days past due',
  '83': '150-179 days past due',
  '84': '180 or more days past due',
  '88': 'Claim filed with the government',
  '89': 'Deed in lieu of foreclosure',
  '93': 'Placed for collection',
  '94': 'Foreclosure completed',
  '95': 'Voluntary surrender',
  '97': 'Charged off',
  DA: 'Delete the account',
  DF: 'Delete the account — reported in error, fraud',
}

/** Special Comment (field 20). */
export const SPECIAL_COMMENTS: Record<string, string> = {
  AC: 'AC — Paying under a partial payment agreement',
  AP: 'AP — Payment deferred',
  AW: 'AW — Affected by a natural or declared disaster',
  BO: 'BO — Foreclosure proceedings started',
  CP: 'CP — In a loan modification trial period',
  CO: 'CO — Loan modified under a federal plan',
  AM: 'AM — Account payments assured by wage garnishment',
  M: 'M — Account closed at the consumer\u2019s request',
}

/** Compliance Condition Code (field 21) — how a dispute is flagged while it is being worked. */
export const COMPLIANCE_CONDITION_CODES: Record<string, string> = {
  XB: 'XB — Account information disputed by the consumer (FCRA)',
  XC: 'XC — Completed investigation, consumer disagrees',
  XH: 'XH — Account previously in dispute, now resolved',
  XR: 'XR — Removes the previous compliance condition code',
}

/** Consumer Information Indicator (field 39) — bankruptcy and similar consumer-level conditions. */
export const CONSUMER_INFORMATION_INDICATORS: Record<string, string> = {
  A: 'A — Petition for Chapter 7 bankruptcy',
  B: 'B — Petition for Chapter 11 bankruptcy',
  C: 'C — Petition for Chapter 12 bankruptcy',
  D: 'D — Petition for Chapter 13 bankruptcy',
  E: 'E — Discharged through Chapter 7',
  H: 'H — Discharged through Chapter 13',
  Q: 'Q — Removes a previous bankruptcy indicator',
  R: 'R — Reaffirmation of debt',
  T: 'T — Credit line closed',
  V: 'V — Chapter 7 bankruptcy dismissed',
}

/** One byte of the 24-month Payment History Profile (field 19). */
export const HISTORY_CODE_LABELS: Record<string, string> = {
  '0': 'Current',
  '1': '30-59 days',
  '2': '60-89 days',
  '3': '90-119 days',
  '4': '120-149 days',
  '5': '150-179 days',
  '6': '180+ days',
  B: 'No history before this',
  D: 'No history this month',
  E: 'Zero balance, current',
  G: 'Collection',
  H: 'Foreclosure completed',
  J: 'Voluntary surrender',
  K: 'Repossession',
  L: 'Charge-off',
}
